/**
 * Qualificação de leads — Poka-Yoke · Venda consultiva
 * Filtra perfis de decisão e capacidade operacional mínima antes do agendamento direto.
 */

export type LeadIntent = 'demo' | 'diagnosis';

export type TeamSizeBand = '1-10' | '11-50' | '51-200' | '201-500' | '500+';

export type ExecutiveRole =
  | 'diretor'
  | 'ceo'
  | 'socio'
  | 'gerente'
  | 'coordenador'
  | 'engenheiro-processos'
  | 'outro';

export interface LeadFormData {
  fullName: string;
  corporateEmail: string;
  executiveRole: ExecutiveRole | '';
  companyName: string;
  phone: string;
  teamSize: TeamSizeBand | '';
  intent: LeadIntent;
}

export type LeadQualificationOutcome = 'qualified' | 'nurture';

export interface LeadSubmissionResult {
  outcome: LeadQualificationOutcome;
  lead: LeadFormData;
  submittedAt: string;
}

/** Perfis com poder de decisão para demonstração técnica imediata */
export const DECISION_MAKER_ROLES: ExecutiveRole[] = [
  'diretor',
  'ceo',
  'socio',
  'gerente',
  'coordenador',
  'engenheiro-processos',
];

export const EXECUTIVE_ROLE_OPTIONS: { value: ExecutiveRole; label: string }[] = [
  { value: 'diretor', label: 'Diretor(a)' },
  { value: 'ceo', label: 'CEO / Presidente' },
  { value: 'socio', label: 'Sócio(a)' },
  { value: 'gerente', label: 'Gerente' },
  { value: 'coordenador', label: 'Coordenador(a)' },
  { value: 'engenheiro-processos', label: 'Engenheiro de Processos' },
  { value: 'outro', label: 'Outro cargo' },
];

export const TEAM_SIZE_OPTIONS: { value: TeamSizeBand; label: string }[] = [
  { value: '1-10', label: 'Até 10 colaboradores operacionais' },
  { value: '11-50', label: '11 a 50 colaboradores operacionais' },
  { value: '51-200', label: '51 a 200 colaboradores operacionais' },
  { value: '201-500', label: '201 a 500 colaboradores operacionais' },
  { value: '500+', label: 'Mais de 500 colaboradores operacionais' },
];

/** Faixas elegíveis para agendamento instantâneo (capacidade mínima alvo) */
const QUALIFIED_TEAM_SIZES: TeamSizeBand[] = ['11-50', '51-200', '201-500', '500+'];

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'yahoo.com',
  'yahoo.com.br',
  'icloud.com',
  'proton.me',
  'protonmail.com',
  'bol.com.br',
  'uol.com.br',
  'terra.com.br',
  'ig.com.br',
  'msn.com',
]);

export const CALENDLY_EMBED_URL =
  import.meta.env.VITE_CALENDLY_DEMO_URL?.trim() ||
  'https://calendly.com/dataflow-enterprise/demonstracao-tecnica';

const EMPTY_FORM: LeadFormData = {
  fullName: '',
  corporateEmail: '',
  executiveRole: '',
  companyName: '',
  phone: '',
  teamSize: '',
  intent: 'demo',
};

export function createEmptyLeadForm(intent: LeadIntent = 'demo'): LeadFormData {
  return { ...EMPTY_FORM, intent };
}

export function isCorporateEmail(email: string): boolean {
  const normalized = email.trim().toLowerCase();
  const match = normalized.match(/^[^\s@]+@([^\s@]+\.[^\s@]+)$/);
  if (!match) return false;
  return !FREE_EMAIL_DOMAINS.has(match[1]);
}

export function isDecisionMakerRole(role: ExecutiveRole | ''): boolean {
  if (!role) return false;
  return DECISION_MAKER_ROLES.includes(role);
}

export function meetsMinimumTeamSize(teamSize: TeamSizeBand | ''): boolean {
  if (!teamSize) return false;
  return QUALIFIED_TEAM_SIZES.includes(teamSize);
}

export function qualifyLead(lead: LeadFormData): LeadQualificationOutcome {
  const roleOk = isDecisionMakerRole(lead.executiveRole);
  const sizeOk = meetsMinimumTeamSize(lead.teamSize);
  const emailOk = isCorporateEmail(lead.corporateEmail);

  return roleOk && sizeOk && emailOk ? 'qualified' : 'nurture';
}

export interface LeadFormErrors {
  fullName?: string;
  corporateEmail?: string;
  executiveRole?: string;
  companyName?: string;
  phone?: string;
  teamSize?: string;
}

export function validateLeadForm(data: LeadFormData): LeadFormErrors {
  const errors: LeadFormErrors = {};

  if (!data.fullName.trim() || data.fullName.trim().length < 3) {
    errors.fullName = 'Informe o nome completo.';
  }

  if (!data.corporateEmail.trim()) {
    errors.corporateEmail = 'Informe o e-mail corporativo.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.corporateEmail.trim())) {
    errors.corporateEmail = 'E-mail inválido.';
  } else if (!isCorporateEmail(data.corporateEmail)) {
    errors.corporateEmail = 'Utilize um e-mail corporativo da sua empresa (não contas pessoais).';
  }

  if (!data.executiveRole) {
    errors.executiveRole = 'Selecione o cargo executivo.';
  }

  if (!data.companyName.trim() || data.companyName.trim().length < 2) {
    errors.companyName = 'Informe o nome da empresa.';
  }

  const phoneDigits = data.phone.replace(/\D/g, '');
  if (!phoneDigits || phoneDigits.length < 10) {
    errors.phone = 'Informe um telefone válido (WhatsApp).';
  }

  if (!data.teamSize) {
    errors.teamSize = 'Selecione o tamanho da equipe operacional.';
  }

  return errors;
}

export function hasFormErrors(errors: LeadFormErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Simula persistência / integração CRM — pronto para HubSpot, Supabase ou Edge Function */
export async function submitLead(data: LeadFormData): Promise<LeadSubmissionResult> {
  const outcome = qualifyLead(data);
  const result: LeadSubmissionResult = {
    outcome,
    lead: data,
    submittedAt: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(
      'dataflow:last-lead',
      JSON.stringify({ ...result, lead: { ...data, phone: '[redacted]' } }),
    );
  } catch {
    /* storage opcional */
  }

  await new Promise((resolve) => setTimeout(resolve, 650));

  return result;
}

export function buildCalendlyEmbedSrc(baseUrl: string, lead: LeadFormData): string {
  const url = new URL(baseUrl);
  url.searchParams.set('embed', 'true');
  url.searchParams.set('hide_gdpr_banner', '1');
  url.searchParams.set('background_color', 'ffffff');
  url.searchParams.set('text_color', '0f172a');
  url.searchParams.set('primary_color', '2563eb');
  url.searchParams.set('name', lead.fullName.trim());
  url.searchParams.set('email', lead.corporateEmail.trim());
  url.searchParams.set('a1', lead.companyName.trim());
  return url.toString();
}
